import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@material-ui/core'
import { useFormik } from 'formik'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AddLesson } from 'src/redux/actions'
import { HIDE_ERROR } from 'src/redux/errorReducer'
import { uploadToS3 } from 'src/util/uploadToS3'
import * as Yup from 'yup'

import { StatusList, VisibilityList } from '../../util/constants'
import { AddVideoImageUrl, generateUniqueFileName, hrs, ImageRatioSize, minutes } from '../../util/helpers'
import Feedback from '../exercise/FormTypes/Feedback'
import FileUpload from '../exercise/FormTypes/Fields/FileUpload'
import VideoThumbnailField from '../exercise/FormTypes/Fields/VideoThumbnailField'
import AsyncAutoComplete from '../helperComponents/AsyncAutoComplete'
import DropdownMenu from '../helperComponents/DropdownMenu'
import LoadingButton from '../helperComponents/LoadingButton'
import useCourseUnitChapterState from './useCourseUnitChapterState'

const validationSchema = Yup.object().shape({
  name: Yup.string().min(3, 'Name must be of atleast 3 characters').max(100, 'Name cannot be more than 100 characters').required('Required'),
  sortOrder: Yup.number().min(0),
  description: Yup.string().min(5, 'Minimum 5 letters').max(200, 'Description cannot be more than 200 characters').required('Required'),
})

const AddEditLesson = ({
  selectedRow = {},
  toggleDrawer,
  refetch,
  setActiveStep,
  firstStepData,
  setFirstStepData,
  actionTitle
}) => {
  const { courseDifficultyLevel } = useSelector(store => store.mainReducer)
  const videoErrorMsg = 'Please enter either video link or select 4L video.'
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showVideoError, setshowVideoError] = useState(false)

  const handleClose = () => {
    dispatch({ type: HIDE_ERROR })
  }

  const { values, ...formik } = useFormik({
    initialValues: {
      name: selectedRow?.name || '',
      description: selectedRow?.description || '',
      video: selectedRow?.video || null,
      fourLVideo: selectedRow?.fourLVideo || '',
      videoType: selectedRow?.videoType || 'NORMAL',
      coin: selectedRow?.coin || '',
      videoIframe: selectedRow?.videoIframe || '',
      course: selectedRow?.course?._id || firstStepData?.languageId || '',
      unit: selectedRow?.unit?._id || firstStepData?.courseId || '',
      chapter: selectedRow?.chapter?._id || firstStepData?.chapterId || '',
      sortOrder: selectedRow?.sortOrder || '',
      difficultyLevel:
        (selectedRow && selectedRow?.difficultyLevel) || firstStepData?.difficultyLevel || 'allLevels',
      isActive:
        selectedRow && selectedRow.isActive === undefined
          ? true
          : selectedRow && selectedRow?.isActive,
      videoThumbnail: selectedRow?.videoThumbnail || '',
      file: selectedRow?.file || '',
      exerciseVideo: selectedRow?.exerciseVideo || '',
      hrs: selectedRow?.hrs ?? "00",
      minutes: selectedRow?.minutes || "00",
      seconds: selectedRow?.seconds || "00",
      isUnLock: selectedRow?.isUnLock || false,
      feedbackQuestion:
        (selectedRow?.lessonFeedback?.length &&
          selectedRow?.lessonFeedback[0]?.feedbackQuestion) ||
        '',
      rating:
        (selectedRow?.lessonFeedback?.length &&
          selectedRow?.lessonFeedback[0]?.rating) ||
        false,
      comment:
        (selectedRow?.lessonFeedback?.length &&
          selectedRow?.lessonFeedback[0]?.comment) ||
        false,
      feedbackPlaceholder:
        (selectedRow?.lessonFeedback?.length &&
          selectedRow?.lessonFeedback[0]?.feedbackPlaceholder) ||
        '',
      unPublished:
        selectedRow && selectedRow?.unPublished === undefined
          ? false
          : selectedRow && selectedRow?.unPublished,
    },
    validationSchema,

    onSubmit: async values => {
      const reqData = selectedRow?._id
        ? {
          ...values,
          lessonFeedback:
            values?.rating || values?.comment
              ? {
                id:
                  (selectedRow?.lessonFeedback?.length &&
                    selectedRow?.lessonFeedback[0]?.id) ||
                  Date.now().toString(36) +
                  Math.random().toString(36).substr(2),
                feedbackQuestion: values.feedbackQuestion,
                rating: values.rating,
                comment: values.comment,
                feedbackPlaceholder: values.feedbackPlaceholder,
              }
              : null,
          _id: selectedRow?._id,
        }
        : {
          ...values,
          lessonFeedback:
            values?.rating || values?.comment
              ? {
                id:
                  Date.now().toString(36) +
                  Math.random().toString(36).substr(2),
                feedbackQuestion: values.feedbackQuestion,
                rating: values.rating,
                comment: values.comment,
                feedbackPlaceholder: values.feedbackPlaceholder,
              }
              : null,
        }
      delete reqData.rating
      delete reqData.comment
      delete reqData.feedbackPlaceholder
      delete reqData.feedbackQuestion
      reqData.hrs = reqData.hrs ? reqData.hrs : 0
      reqData.minutes = reqData.minutes ? reqData.minutes : 0
      reqData.seconds = reqData.seconds ? reqData.seconds : 0
      if (!reqData.video && !reqData.exerciseVideo.length && !reqData.fourLVideo) {
        setshowVideoError(true)
      } else {
        setIsSubmitting(true)
        for (let key in reqData) {
          if (reqData[key] instanceof File) {
            let file = reqData[key]
            const uniqueName = generateUniqueFileName(file);
            const uniqueFile = new File([file], uniqueName, { type: file.type });
            await uploadToS3(uniqueFile);
            reqData[key] = uniqueName;
          }
        }
        const response = await dispatch(AddLesson(reqData));
        if (!response) return;

        switch (actionTitle) {
          case "create-lesson":
            setFirstStepData({
              lessonId: response?._id,
              course: response?.course,
              chapter: response?.chapter,
              unit: response?.unit,
            });
            setActiveStep(1);
            break;

          case "create-chapter":
            setActiveStep(2);
            break;

          default:
            toggleDrawer();
            break;
        }

        refetch();
      }
    },
  })

  const { chaptersList, coursesList, unitsList } =
    useCourseUnitChapterState(values)

  const dispatch = useDispatch()
  // const [totalUnits, setTotalUnits] = useState([])
  // const [totalCourses, setTotalCourses] = useState([])

  // useEffect(() => {
  //   setTotalUnits(dropdowns?.units)
  //   setTotalCourses(dropdowns?.courses)
  // }, [dropdowns])

  const onCourseChange = ({ target: { value } }) => {
    // setTotalUnits(filterByKeyValue(dropdowns?.units, 'course', value))
    formik.setValues({ ...values, chapter: '', unit: '', course: value })
  }

  const onUnitChange = ({ target: { value } }) => {
    // setTotalUnits(filterByKeyValue(dropdowns?.units, 'course', value))
    formik.setValues({ ...values, chapter: '', unit: value })
  }

  const handleCancel = () => {
    toggleDrawer();
  }

  const onStatusChange = ({ target: { value } }) => {
    formik.setValues({ ...values, isActive: value })
  }
  const onisUnLockChange = ({ target: { checked } }) => {
    formik.setValues({ ...values, isUnLock: checked })
  }
  const onThumbnailChange = ({ target: { value, files } }) => {
    formik.setValues({ ...values, videoThumbnail: files[0] })
  }

  const onFileUpload = e => {
    formik.setValues({ ...values, file: e.target.files[0] })
  }

  return (
    <>
      <Box sx={{ m: 1 }}>
        <Container>
          {/* <Typography color="textPrimary" variant="h4">
            {selectedRow?.id ? 'Edit' : 'Add'} a Lesson
          </Typography> */}
          <form onSubmit={formik.handleSubmit} style={{ width: "100%" }}>
            <Grid container spacing={3}>
              {/* Column 1 */}
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <TextField
                    error={Boolean(formik.touched.name && formik.errors.name)}
                    fullWidth
                    size="small"
                    helperText={formik.touched.name && formik.errors.name}
                    label="Name"
                    name="name"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={values.name}
                    variant="outlined"
                    required
                    margin="none"

                  />

                  <TextField
                    multiline
                    rows={4}
                    error={Boolean(formik.touched.description && formik.errors.description)}
                    fullWidth
                    helperText={formik.touched.description && formik.errors.description}
                    label="Description"
                    name="description"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={values.description}
                    variant="outlined"
                    required
                    margin="none"
                  />

                  <FormControl fullWidth sx={{ my: 0 }}>
                    <DropdownMenu
                      value={values.course}
                      label="Language"
                      name="course"
                      onChange={onCourseChange}
                      isRequired
                      data={coursesList}
                    />
                  </FormControl>

                  <FormControl fullWidth sx={{ my: 0 }}>
                    <DropdownMenu
                      value={values.unit}
                      label="Course"
                      name="unit"
                      isRequired
                      onChange={onUnitChange}
                      data={unitsList}
                    />
                  </FormControl>

                  <FormControl fullWidth sx={{ my: 0 }}>
                    <DropdownMenu
                      value={values.chapter}
                      label="Chapter"
                      name="chapter"
                      isRequired
                      onChange={formik.handleChange}
                      data={chaptersList}
                    />
                  </FormControl>

                  <FormControl fullWidth sx={{ my: 0 }}>
                    <DropdownMenu
                      value={values.difficultyLevel}
                      label="Difficulty Level"
                      name="difficultyLevel"
                      onChange={formik.handleChange}
                      data={courseDifficultyLevel}
                    />
                  </FormControl>

                  <Typography variant="subtitle1">Duration of Lesson</Typography>

                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    <FormControlLabel
                      label={<Typography>HH</Typography>}
                      control={
                        <DropdownMenu
                          value={values.hrs}
                          name="hrs"
                          onChange={formik.handleChange}
                          isRequired
                          data={hrs}
                        />
                      }
                    />
                    <FormControlLabel
                      label={<Typography>MM</Typography>}
                      control={
                        <DropdownMenu
                          value={values.minutes}
                          name="minutes"
                          onChange={formik.handleChange}
                          isRequired
                          data={minutes}
                        />
                      }
                    />
                    <FormControlLabel
                      label={<Typography>SS</Typography>}
                      control={
                        <DropdownMenu
                          value={values.seconds}
                          name="seconds"
                          onChange={formik.handleChange}
                          isRequired
                          data={minutes}
                        />
                      }
                    />
                  </Stack>

                  <FormControlLabel
                    control={
                      <Checkbox
                        defaultChecked={values.isUnLock}
                        checked={values.isUnLock}
                        onChange={onisUnLockChange}
                      />
                    }
                    label="Available for Free Members"
                  />

                  <FormControl fullWidth sx={{ my: 0 }}>
                    <DropdownMenu
                      value={values.isActive}
                      label="Status"
                      name="isActive"
                      isRequired
                      onChange={onStatusChange}
                      data={StatusList}
                    />
                  </FormControl>
                </Stack>

              </Grid>

              {/* Column 2 */}
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <FileUpload onChange={onFileUpload} fileName={values?.file} />

                  <Box width="full">
                    <VideoThumbnailField
                      imageUrl={values?.videoThumbnail}
                      onChange={onThumbnailChange}
                    />
                    <ImageRatioSize ratio="46∶47" minWidth="230px" size="500kb" />
                  </Box>

                  <Stack direction="row" spacing={2}>
                    <FormControlLabel
                      label="Normal Video"
                      control={
                        <Radio
                          name="videoType"
                          onChange={formik.handleChange}
                          checked={values.videoType === "NORMAL"}
                          value="NORMAL"
                        />
                      }
                    />
                    <FormControlLabel
                      label="4L Video"
                      control={
                        <Radio
                          name="videoType"
                          onChange={formik.handleChange}
                          checked={values.videoType === "4L"}
                          value="4L"
                        />
                      }
                    />
                  </Stack>

                  {values.videoType === "NORMAL" ? (
                    <Box>
                      <TextField
                        error={Boolean(formik.touched.video && formik.errors.video)}
                        fullWidth
                        size="small"
                        helperText={formik.touched.video && formik.errors.video}
                        label="Video URL"
                        type="url"
                        placeholder="Video Link"
                        margin="none"
                        name="video"
                        onBlur={formik.handleBlur}
                        onChange={(event) => {
                          formik.handleChange(event);
                          formik.setFieldValue("fourLVideo", "");
                        }}
                        value={values.video}
                        variant="outlined"
                      />
                      <AddVideoImageUrl text="Add full YouTube or video URL." />
                    </Box>
                  ) : (
                    <FormControl fullWidth margin="none" size='small'>
                      <AsyncAutoComplete
                        placeholder="Min. 3 characters to search"
                        value={values.fourLVideo}
                        label="Search 4L Videos"
                        name="fourLVideo"
                        isRequired
                        onChange={(selectedOption) => {
                          formik.setFieldValue(
                            "fourLVideo",
                            selectedOption ? selectedOption.id : ""
                          );
                          formik.setFieldValue("video", null);
                        }}
                        apiName="videos"
                        size='small'
                      />
                    </FormControl>
                  )}

                  <Feedback values={values} onChange={formik.handleChange} />

                  <TextField
                    fullWidth
                    size="small"
                    label="Video Iframe"
                    multiline
                    rows={2}
                    placeholder="Video Iframe"
                    name="videoIframe"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={values.videoIframe}
                    variant="outlined"
                    margin="none"
                  />

                  <TextField
                    error={Boolean(formik.touched.coin && formik.errors.coin)}
                    fullWidth
                    size="small"
                    helperText={formik.touched.coin && formik.errors.coin}
                    label="Video Coin"
                    placeholder="Video Coin"
                    name="coin"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={values.coin}
                    variant="outlined"
                    type="number"
                    margin="none"
                  />

                  <TextField
                    fullWidth
                    size="small"
                    label="Sort Order"
                    margin="none"
                    inputProps={{ min: 0 }}
                    name="sortOrder"
                    type="number"
                    defaultValue={0}
                    onChange={formik.handleChange}
                    value={values?.sortOrder}
                    variant="outlined"
                  />

                  <FormControl fullWidth margin="none">
                    <DropdownMenu
                      value={values.unPublished}
                      label="Visibility"
                      name="unPublished"
                      isRequired
                      onChange={formik.handleChange}
                      data={VisibilityList}
                    />
                  </FormControl>

                  {showVideoError && (
                    <Box color="red" padding="10px">
                      {videoErrorMsg}
                    </Box>
                  )}
                </Stack>
              </Grid>

              {/* Buttons */}
              <Grid item xs={12} md={6}>
                <Button
                  color="primary"
                  onClick={handleCancel}
                  fullWidth
                  size="large"
                  type="button"
                  variant="outlined"
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <LoadingButton
                  color="primary"
                  disabled={isSubmitting}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  name="Save"
                >
                </LoadingButton>
              </Grid>
            </Grid>
          </form>
        </Container>
      </Box>
    </>
  )
}

export default AddEditLesson
